import { useState } from 'react';
import { Link } from 'react-router-dom';
import { DayritLogo } from '../components/ui';
import api from '../api';

function Spinner() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" style={{ animation: 'spin 0.7s linear infinite', flexShrink: 0 }}>
      <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/>
    </svg>
  );
}

function FieldInput({ label, type = 'text', placeholder, value, onChange, error, autoFocus, hint }) {
  const [focused, setFocused] = useState(false);
  return (
    <div>
      <label style={{ display:'block', fontSize:13, fontWeight:600, color:'var(--text2)', marginBottom:6 }}>{label}</label>
      <input type={type} placeholder={placeholder} value={value} onChange={onChange} autoFocus={autoFocus}
        onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}
        style={{
          width:'100%', boxSizing:'border-box', padding:'11px 14px',
          fontSize:14, fontFamily:'inherit',
          border:`1.5px solid ${error ? '#EF4444' : focused ? 'var(--accent)' : 'var(--border)'}`,
          borderRadius:10, background:'var(--input-bg)', color:'var(--text)', outline:'none',
          boxShadow: focused && !error ? '0 0 0 3px var(--accent-ring)' : 'none', transition:'all 0.15s',
        }}
      />
      {error && <p style={{ fontSize:12, color:'#EF4444', marginTop:4, fontWeight:500 }}>{error}</p>}
      {hint && !error && <p style={{ fontSize:12, color:'var(--text3)', marginTop:4 }}>{hint}</p>}
    </div>
  );
}

function PasswordInput({ label, placeholder, value, onChange, error, hint, autoFocus }) {
  const [show, setShow] = useState(false);
  const [focused, setFocused] = useState(false);
  return (
    <div>
      <label style={{ display:'block', fontSize:13, fontWeight:600, color:'var(--text2)', marginBottom:6 }}>{label}</label>
      <div style={{ position:'relative' }}>
        <input type={show ? 'text' : 'password'} placeholder={placeholder} value={value} onChange={onChange} autoFocus={autoFocus}
          onFocus={() => setFocused(true)} onBlur={() => setFocused(false)}
          style={{
            width:'100%', boxSizing:'border-box', padding:'11px 42px 11px 14px',
            fontSize:14, fontFamily:'inherit',
            border:`1.5px solid ${error ? '#EF4444' : focused ? 'var(--accent)' : 'var(--border)'}`,
            borderRadius:10, background:'var(--input-bg)', color:'var(--text)', outline:'none',
            boxShadow: focused && !error ? '0 0 0 3px var(--accent-ring)' : 'none', transition:'all 0.15s',
          }}
        />
        <button type="button" onClick={() => setShow(p => !p)}
          style={{ position:'absolute', right:12, top:'50%', transform:'translateY(-50%)', background:'none', border:'none', cursor:'pointer', color:'var(--text3)', padding:0, display:'flex' }}>
          {show
            ? <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19m-6.72-1.07a3 3 0 11-4.24-4.24"/><line x1="1" y1="1" x2="23" y2="23"/></svg>
            : <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>
          }
        </button>
      </div>
      {error && <p style={{ fontSize:12, color:'#EF4444', marginTop:4, fontWeight:500 }}>{error}</p>}
      {hint && !error && <p style={{ fontSize:12, color:'var(--text3)', marginTop:4 }}>{hint}</p>}
    </div>
  );
}

function StepDots({ step }) {
  const steps = ['request', 'sent', 'reset', 'done'];
  const idx   = steps.indexOf(step);
  return (
    <div style={{ display:'flex', gap:6, justifyContent:'center', marginBottom:28 }}>
      {steps.slice(0, 3).map((s, i) => (
        <div key={s} style={{
          height:4, borderRadius:2,
          width: i === idx ? 20 : 6,
          background: i <= idx ? 'var(--accent)' : 'var(--border)',
          transition:'all 0.3s',
        }}/>
      ))}
    </div>
  );
}

function PrimaryButton({ children, onClick, loading, disabled, type = 'button' }) {
  return (
    <button type={type} onClick={onClick} disabled={disabled || loading} style={{
      display:'flex', alignItems:'center', justifyContent:'center', gap:8,
      width:'100%', padding:'13px 20px', borderRadius:11, border:'none',
      background: (disabled || loading) ? 'var(--border)' : 'var(--brand-grad)',
      color: (disabled || loading) ? 'var(--text3)' : 'white',
      fontSize:15, fontWeight:700, cursor:(disabled || loading) ? 'not-allowed' : 'pointer',
      fontFamily:'inherit', transition:'all 0.15s', minHeight:48,
      boxShadow: (!disabled && !loading) ? '0 4px 16px var(--accent-ring)' : 'none',
    }}
      onMouseEnter={e => { if (!disabled && !loading) e.currentTarget.style.filter = 'brightness(1.08)'; }}
      onMouseLeave={e => e.currentTarget.style.filter = ''}
    >{children}</button>
  );
}

function ErrorBox({ message }) {
  if (!message) return null;
  return (
    <div style={{ display:'flex', alignItems:'center', gap:8, padding:'10px 13px', borderRadius:9, background:'rgba(239,68,68,0.07)', border:'1px solid rgba(239,68,68,0.2)', fontSize:13, color:'#DC2626', fontWeight:500 }}>
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" style={{ flexShrink:0 }}>
        <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
      </svg>
      {message}
    </div>
  );
}

// FIX: helper to change step and always clear error
function useStepNav(setStep, setError) {
  return (nextStep) => {
    setError('');
    setStep(nextStep);
  };
}

export default function ForgotPasswordPage() {
  const [step,    setStep]    = useState('request');
  const [email,   setEmail]   = useState('');
  const [token,   setToken]   = useState('');
  const [form,    setForm]    = useState({ password: '', password_confirmation: '' });
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');

  // FIX: always clear error when navigating between steps
  const goTo = useStepNav(setStep, setError);

  // ── Step 1: send reset email ──────────────────────────────────────────
  const handleRequest = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true); setError('');
    try {
      await api.post('/auth/forgot-password', { email });
      goTo('sent');
    } catch (err) {
      setError(err.response?.data?.message || 'Could not send reset email. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ── Step 2: validate token before showing password form ───────────────
  // We don't hit the API here — just move forward. The server validates
  // the token in step 3. If wrong, the error shows clearly on the reset form.
  const handleTokenContinue = () => {
    if (!token.trim()) return;
    goTo('reset');
  };

  // ── Step 3: submit new password ───────────────────────────────────────
  const handleReset = async (e) => {
    e.preventDefault();
    if (form.password.length < 8) { setError('Password must be at least 8 characters.'); return; }
    if (form.password !== form.password_confirmation) { setError('Passwords do not match.'); return; }
    setLoading(true); setError('');
    try {
      await api.post('/auth/reset-password', {
        email,
        token,
        password:              form.password,
        password_confirmation: form.password_confirmation,
      });
      goTo('done');
    } catch (err) {
      // FIX: if token is invalid/expired, go back to 'sent' step so user can re-enter token
      const msg = err.response?.data?.message || 'Reset failed. The token may have expired.';
      const isTokenError = err.response?.status === 422 &&
        (msg.toLowerCase().includes('invalid') || msg.toLowerCase().includes('expired') || msg.toLowerCase().includes('token'));
      setError(msg);
      if (isTokenError) {
        setToken('');
        goTo('sent');
        // Re-set the error after goTo clears it
        setTimeout(() => setError(msg), 0);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ minHeight:'100vh', background:'var(--bg)', display:'flex', alignItems:'center', justifyContent:'center', padding:'clamp(16px,4vw,40px)', fontFamily:"-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif" }}>
      <style>{`
        @keyframes spin { to { transform: rotate(360deg) } }
        @keyframes fadeIn { from { opacity:0; transform:translateY(6px) } to { opacity:1; transform:none } }
      `}</style>

      <div style={{ width:'100%', maxWidth:400 }}>

        {/* Logo */}
        <Link to="/" style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:9, marginBottom:32, textDecoration:'none' }}>
          <DayritLogo size={30}/>
          <span style={{ fontSize:17, fontWeight:800, background:'var(--brand-grad)', WebkitBackgroundClip:'text', WebkitTextFillColor:'transparent' }}>Dayrit</span>
        </Link>

        <div style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:16, padding:'clamp(24px,5vw,36px)', boxShadow:'0 4px 24px rgba(0,0,0,0.06)', animation:'fadeIn 0.3s ease' }}>

          {step !== 'done' && <StepDots step={step}/>}

          {/* ── Step 1: Enter email ── */}
          {step === 'request' && (
            <div style={{ animation:'fadeIn 0.25s ease' }}>
              <div style={{ textAlign:'center', marginBottom:24 }}>
                <div style={{ fontSize:40, marginBottom:12 }}>🔑</div>
                <h1 style={{ fontSize:22, fontWeight:800, color:'var(--text)', letterSpacing:'-0.5px', marginBottom:6 }}>Forgot your password?</h1>
                <p style={{ fontSize:14, color:'var(--text3)', lineHeight:1.55 }}>Enter your email and we'll send you a reset code.</p>
              </div>
              <form onSubmit={handleRequest} style={{ display:'flex', flexDirection:'column', gap:16 }}>
                <FieldInput label="Email address" type="email" placeholder="you@example.com"
                  value={email} onChange={e => setEmail(e.target.value)} autoFocus/>
                <ErrorBox message={error}/>
                <PrimaryButton type="submit" loading={loading}>
                  {loading ? <><Spinner/>Sending…</> : 'Send reset code →'}
                </PrimaryButton>
              </form>
            </div>
          )}

          {/* ── Step 2: Paste token ── */}
          {step === 'sent' && (
            <div style={{ animation:'fadeIn 0.25s ease' }}>
              <div style={{ textAlign:'center', marginBottom:24 }}>
                <div style={{ fontSize:40, marginBottom:12 }}>📬</div>
                <h1 style={{ fontSize:22, fontWeight:800, color:'var(--text)', letterSpacing:'-0.5px', marginBottom:8 }}>Check your inbox</h1>
                <p style={{ fontSize:14, color:'var(--text3)', lineHeight:1.6 }}>
                  We sent a reset code to{' '}
                  <strong style={{ color:'var(--text)' }}>{email}</strong>.
                  <br/>Paste it below to continue.
                </p>
              </div>
              <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
                <FieldInput
                  label="Reset code from email"
                  placeholder="Paste your code here"
                  value={token}
                  onChange={e => setToken(e.target.value)}
                  autoFocus
                  hint="Can't find it? Check your spam folder."
                />
                <ErrorBox message={error}/>
                <PrimaryButton onClick={handleTokenContinue} disabled={!token.trim()}>
                  Continue →
                </PrimaryButton>
                <button onClick={() => goTo('request')}
                  style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text3)', fontSize:13, fontFamily:'inherit', padding:'6px 0' }}>
                  ← Try a different email
                </button>
              </div>
            </div>
          )}

          {/* ── Step 3: New password ── */}
          {step === 'reset' && (
            <div style={{ animation:'fadeIn 0.25s ease' }}>
              <div style={{ textAlign:'center', marginBottom:24 }}>
                <div style={{ fontSize:40, marginBottom:12 }}>🔒</div>
                <h1 style={{ fontSize:22, fontWeight:800, color:'var(--text)', letterSpacing:'-0.5px', marginBottom:6 }}>Set new password</h1>
                <p style={{ fontSize:14, color:'var(--text3)' }}>Create a strong new password for your account.</p>
              </div>
              <form onSubmit={handleReset} style={{ display:'flex', flexDirection:'column', gap:16 }}>
                <PasswordInput
                  label="New password" placeholder="Min. 8 characters"
                  value={form.password}
                  onChange={e => setForm(p => ({ ...p, password: e.target.value }))}
                  hint="At least 8 characters" autoFocus
                />
                <PasswordInput
                  label="Confirm new password" placeholder="Repeat your password"
                  value={form.password_confirmation}
                  onChange={e => setForm(p => ({ ...p, password_confirmation: e.target.value }))}
                />
                <ErrorBox message={error}/>
                <PrimaryButton type="submit" loading={loading}>
                  {loading ? <><Spinner/>Resetting…</> : 'Reset password →'}
                </PrimaryButton>
                <button type="button" onClick={() => goTo('sent')}
                  style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text3)', fontSize:13, fontFamily:'inherit', padding:'6px 0' }}>
                  ← Back to token entry
                </button>
              </form>
            </div>
          )}

          {/* ── Step 4: Success ── */}
          {step === 'done' && (
            <div style={{ textAlign:'center', animation:'fadeIn 0.3s ease' }}>
              <div style={{
                width:72, height:72, borderRadius:'50%', margin:'0 auto 20px',
                background:'linear-gradient(135deg, #16A34A, #4ade80)',
                display:'flex', alignItems:'center', justifyContent:'center',
                boxShadow:'0 8px 24px rgba(22,163,74,0.3)',
              }}>
                <svg width="32" height="26" viewBox="0 0 32 26" fill="none">
                  <path d="M2 13L11 22L30 3" stroke="white" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h2 style={{ fontSize:22, fontWeight:800, color:'var(--text)', letterSpacing:'-0.5px', marginBottom:8 }}>Password updated!</h2>
              <p style={{ fontSize:14, color:'var(--text3)', lineHeight:1.6, marginBottom:24 }}>
                Your password has been reset successfully.<br/>Sign in with your new password.
              </p>
              <Link to="/login" style={{
                display:'flex', alignItems:'center', justifyContent:'center', gap:8,
                padding:'13px 20px', borderRadius:11, textDecoration:'none',
                background:'var(--brand-grad)', color:'white',
                fontSize:15, fontWeight:700, minHeight:48,
                boxShadow:'0 4px 16px var(--accent-ring)',
              }}>
                Sign in →
              </Link>
            </div>
          )}
        </div>

        {step !== 'done' && (
          <p style={{ textAlign:'center', marginTop:18, fontSize:13, color:'var(--text3)' }}>
            Remember it?{' '}
            <Link to="/login" style={{ color:'var(--accent)', fontWeight:700, textDecoration:'none' }}>Back to sign in</Link>
          </p>
        )}
      </div>
    </div>
  );
}